"use client";
import React, { useState } from "react";
import {
  Modal,
  ModalBody,
  ModalContent,
  useDisclosure,
} from "@nextui-org/react";
import { useDictionary } from "../../context/dictionaryProvider";
import { ContactWrapShape, PrimaryBtn, SendBtn } from "../molecules";
import InputTextField from "../atoms/InputTextField";
import SelectField from "../atoms/SelectField";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { TeamUpskillAction } from "../../actions/singles";
import { bitrixLeadAction } from "../../actions/bitrix";
import { bitrixSourceIds, senderEmail } from "../../lib/util";
import ThankuModal from "./ThankuModal";
import Loading from "../../app/[lang]/loading";
import PhoneField from "../atoms/PhoneField";
import { useSearchParams } from "next/navigation";
import { isPhoneValid } from "../../lib/validatePhone";
import { leadTemplate } from "../../emailTemplates/leadTemplate";
import { emailAction } from "../../actions/email";

const formSchema = yup
  .object({
    name: yup
      .string()
      .required()
      .min(3, "Name must be at least 3 characters long")
      .max(25, "Name cannot exceed 25 characters")
      .matches(/^[a-zA-Z0-9\s]+$/, "No special character allowed"),
    email: yup
      .string()
      .email()
      .required()
      .matches(
        /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
        "Email must have a valid domain"
      ),
    company_name: yup
      .string()
      .required("Company Name is required"),
    preferred_time_callback: yup
      .string(),
    start_date: yup
      .date()
      .required("Tentative start date is required")
      .typeError("Please enter a valid date"),
    phone: yup
      .string()
      .required("Phone number is required")
      .test("is-valid-phone", "Phone number is not valid", (value) => {
        if (!value) return false; // Handle empty value if required
        return isPhoneValid(value);
      }),
    message: yup
      .string()
      .trim()
      .required()
      .matches(/^[a-zA-Z0-9\s]+$/, "No special character allowed"),
  })
  .required();

const UpskillYourTeam = ({ type, className = null }) => {
  const searchParams = useSearchParams();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const dict = useDictionary();
  const [response, setResponse] = useState("");
  const [thankyou, setThankyou] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    trigger,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    resolver: yupResolver(formSchema),
  });

  const onSubmit = async (data) => {
    try {
      let res = await TeamUpskillAction({
        ...data,
        phone: data?.phone?.toString(),
      });
      
      await bitrixLeadAction(data, searchParams, bitrixSourceIds.bespoke);

      if (res?.error) {
        return setResponse(res?.error?.message);
      }

      if (res) setThankyou((prev) => !prev);

      try {
        const html = leadTemplate({ type: "Upskills", ...data });
        await emailAction(senderEmail, html);
      } catch (error) {
        console.log(`Error while sending email`, error?.message);
      }

      reset();
    } catch (error) {
      console.log(
        `Error while submitting form: TeamUpskillAction`,
        error?.message
      );
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();
    onOpen();
  };
  const splitName = (fullName) => {
    const nameParts = fullName.trim().split(" ");
    return {
      firstName: nameParts[0] || "",
      lastName: nameParts.slice(1).join(" ") || "", 
    };
  };
  const trackUpSkillFormClick = (menuName, name, email, phone, message,pax, preferred_time_callback, start_date, company_name) => {
    const { firstName, lastName } = splitName(name);
    
    webengage.user.setAttribute("we_email", email);
    webengage.user.setAttribute("we_first_name", firstName);
    webengage.user.setAttribute("we_last_name", lastName);
    webengage.user.setAttribute("we_phone", phone);
    webengage.user.login(email);
    webengage.track('UpskillForm', {
      category: 'UpskillForm',
      label: menuName,
      value: 1,
      userName: name, 
      userEmail: email, 
      userPhone: phone,
      userMessage: message,
      userCompanyName: company_name,
      pax: pax,
      preferredCallback: preferred_time_callback,
      startDates: start_date,
      upskillTraining: "Corporate Enquiry"
    });
  };
  return (
    <>
      {/* <PrimaryBtn onClick={handleClick}>{dict?.upskillTeam}</PrimaryBtn> */}
      <PrimaryBtn
        onClick={handleClick}
        className={className || "border border-primary"}
      >
        {dict?.upskillTeam}
      </PrimaryBtn>

      {isOpen && (
        <Modal
          backdrop="blur"
          isOpen={isOpen}
          size="xl"
          classNames={{
            backdrop: "blur-md",
          }}
          scrollBehavior="outside"
          onOpenChange={onOpenChange}
        >
          <ModalContent>
            {(onClose) => (
              <>
                {!isSubmitting ? (
                  <ModalBody>
                    {!thankyou ? (
                      <form
                        onSubmit={handleSubmit(onSubmit)}
                        className="relative bg-[#fff] my-12 mx:0 md:mx-10"
                      >
                        <div className="px-12 pb-28 pt-10 rounded-2xl border border-black">
                          <h3 className="text-2xl text-[#303030] mb-10 font-bold">
                            {dict?.upskillTeam}
                          </h3>
                          <InputTextField
                            label={dict?.form.fieldNames.name}
                            type={"text"}
                            isInvalid={!!errors?.name}
                            errorMessage={errors?.name?.message}
                            {...register("name")}
                          />
                          <InputTextField
                            label={dict?.form.fieldNames.work_email}
                            type={"email"}
                            isInvalid={!!errors?.email}
                            errorMessage={errors?.email?.message}
                            {...register("email")}
                          />
                          <InputTextField
                            label={dict?.form.fieldNames.company_name}
                            type={"text"}
                            isInvalid={!!errors?.company_name}
                            errorMessage={errors?.company_name?.message}
                            {...register("company_name")}
                          />
                          <SelectField
                            label={dict?.form.fieldNames.pax || "Pax"}
                            options={[
                              { label: "1", value: "1" },
                              { label: "2-5", value: "2-5" },
                              { label: "6-10", value: "6-10" },
                              { label: "10+", value: "10+" },
                            ]}
                            placeholder={"Select pax"}
                            onChange={(value) =>
                              setValue("pax", value, {
                                shouldDirty: true,
                                shouldValidate: isDirty,
                              })
                            }
                            value={getValues("pax")}
                            isInvalid={!!errors?.pax}
                            errorMessage={errors?.pax?.message}
                          />
                          <div className="mb-6">
                            <label className="block text-sm font-bold text-black-700 mb-1">
                              {dict?.form.fieldNames.start_date || "Tentative Start Date"}
                            </label>
                            <input
                              type="date"
                              {...register("start_date")}
                              className={`w-full border px-3 py-2 rounded-md focus:outline-none ${
                                errors?.start_date ? "border-red-500" : "border-gray-300"
                              }`}
                            />
                            {errors?.start_date && (
                              <p className="text-sm text-red-500 mt-1">{errors.start_date.message}</p>
                            )}
                          </div>
                          <InputTextField
                            label={dict?.form.fieldNames.preferred_time_callback}
                            type={"text"}
                            isInvalid={!!errors?.preferred_time_callback}
                            errorMessage={errors?.preferred_time_callback?.message}
                            {...register("preferred_time_callback")}
                          />
                          <PhoneField
                            label={dict?.form.fieldNames.phone}
                            value={getValues("phone")}
                            onChange={async (value) => {
                              setValue("phone", value, {
                                shouldDirty: true,
                                shouldValidate: isDirty,
                              });
                            }}
                            isInvalid={!!errors?.phone}
                            errorMessage={errors?.phone?.message}
                          />
                          <InputTextField
                            label={dict?.form.fieldNames.message}
                            type={"text"}
                            isInvalid={!!errors?.message}
                            errorMessage={errors?.message?.message}
                            {...register("message")}
                          />
                          {response}
                        </div>
                        <SendBtn
                          type="submit"
                          className="!p-4"
                          id="upskill-your-team"
                          onClick={() => {
                            trackUpSkillFormClick(
                              dict?.upskillTeam || 'Upskill Your Team',
                              getValues().name,
                              getValues().email,
                              getValues().phone,
                              getValues().message,
                              getValues().preferred_time_callback,
                              getValues().start_date,
                              getValues().pax,
                              getValues().company_name
                            );
                          }}
                        >
                          {dict?.sendNow}
                        </SendBtn>
                        <ContactWrapShape />
                      </form>
                    ) : (
                      <ThankuModal
                        message={
                          "Thank you for submitting your query! Our team will get back to you soon!"
                        }
                        btnText={"Close"}
                        handleClick={() => {
                          onOpenChange();
                          setThankyou((prev) => !prev);
                        }}
                      />
                    )}
                  </ModalBody>
                ) : (
                  <Loading />
                )}
              </>
            )}
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default UpskillYourTeam;
